// Viewer-local FT3D graphics-quality contract. This is intentionally entitlement-neutral: a future
// account tier may change which choices are offered, but it must not silently rewrite the meaning of
// a stored choice. Texture package selection belongs before fetch; persistent surface representation
// manifests now resolve Low/Medium derivatives or native High/Ultra bytes before map download.

export const FT3D_GRAPHICS_QUALITY_STORAGE_KEY = 'ft3d.graphicsQuality.v1';
export const FT3D_GRAPHICS_QUALITY_QUERY_KEY = 'ft3d_graphics_quality';

export const FT3D_GRAPHICS_QUALITIES = ['low', 'medium', 'high', 'ultra'] as const;
export type Ft3dGraphicsQuality = typeof FT3D_GRAPHICS_QUALITIES[number];

export type Ft3dGraphicsQualityDefinition = Readonly<{
    value: Ft3dGraphicsQuality;
    label: string;
    terrainTextureResolutionPx: 1024 | 2048 | 4096;
    summary: string;
}>;

export const FT3D_GRAPHICS_QUALITY_DEFINITIONS: readonly Ft3dGraphicsQualityDefinition[] = Object.freeze([
    Object.freeze({
        value: 'low',
        label: 'Low · 1K',
        terrainTextureResolutionPx: 1024,
        summary: 'Lower terrain memory and composition cost; intended for phones and constrained devices.',
    }),
    Object.freeze({
        value: 'medium',
        label: 'Medium · 2K',
        terrainTextureResolutionPx: 2048,
        summary: 'Balanced terrain detail and load cost. This is the default.',
    }),
    Object.freeze({
        value: 'high',
        label: 'High · 4K',
        terrainTextureResolutionPx: 4096,
        summary: 'Native 4K terrain composition with substantially higher memory use.',
    }),
    Object.freeze({
        value: 'ultra',
        label: 'Ultra · 4K',
        terrainTextureResolutionPx: 4096,
        summary: 'The future maximum-quality ceiling; terrain resolution currently matches High.',
    }),
]);

export function normalizeFt3dGraphicsQuality(value: unknown): Ft3dGraphicsQuality {
    const normalized = String(value ?? '').trim().toLowerCase();
    return (FT3D_GRAPHICS_QUALITIES as readonly string[]).includes(normalized)
        ? normalized as Ft3dGraphicsQuality
        : 'medium';
}

export function readFt3dGraphicsQuality(): Ft3dGraphicsQuality {
    try {
        const queryValue = new URLSearchParams(window.location.search).get(FT3D_GRAPHICS_QUALITY_QUERY_KEY);
        if (queryValue) return normalizeFt3dGraphicsQuality(queryValue);
    } catch { /* an opaque embedding still gets the stored/default choice */ }
    try {
        return normalizeFt3dGraphicsQuality(window.localStorage.getItem(FT3D_GRAPHICS_QUALITY_STORAGE_KEY));
    } catch {
        return 'medium';
    }
}

export function writeFt3dGraphicsQuality(value: Ft3dGraphicsQuality): void {
    try { window.localStorage.setItem(FT3D_GRAPHICS_QUALITY_STORAGE_KEY, value); } catch { /* viewer-local */ }
}

export function ft3dGraphicsQualityDefinition(
    value: Ft3dGraphicsQuality,
): Ft3dGraphicsQualityDefinition {
    return FT3D_GRAPHICS_QUALITY_DEFINITIONS.find((definition) => definition.value === value)
        ?? FT3D_GRAPHICS_QUALITY_DEFINITIONS[1]!;
}

export type Ft3dGraphicsQualityRuntime = Readonly<{
    requested: Ft3dGraphicsQuality;
    effective: Ft3dGraphicsQuality;
    terrainTextureResolutionPx: number;
    deviceTextureLimitPx: number | null;
    deviceCapped: boolean;
}>;

/** Resolve once before material cache lookup so differently sized canvases can never share an entry. */
export function resolveFt3dGraphicsQualityRuntime(maxTextureSize?: number): Ft3dGraphicsQualityRuntime {
    const requested = readFt3dGraphicsQuality();
    const requestedResolution = ft3dGraphicsQualityDefinition(requested).terrainTextureResolutionPx;
    const finiteLimit = Number.isFinite(maxTextureSize) && Number(maxTextureSize) > 0
        ? Math.floor(Number(maxTextureSize))
        : null;
    const supportedResolution = finiteLimit === null || finiteLimit >= 4096
        ? requestedResolution
        : finiteLimit >= 2048
            ? Math.min(requestedResolution, 2048)
            : Math.min(requestedResolution, 1024);
    const effective: Ft3dGraphicsQuality = supportedResolution >= 4096
        ? requested === 'ultra' ? 'ultra' : 'high'
        : supportedResolution >= 2048
            ? 'medium'
            : 'low';
    return Object.freeze({
        requested,
        effective,
        terrainTextureResolutionPx: supportedResolution,
        deviceTextureLimitPx: finiteLimit,
        deviceCapped: supportedResolution < requestedResolution,
    });
}
